<template>
  <comment-layout
    class="comment-item"
    :creator="comment.creator"
    :show-meta="comment.showMeta"
    :timestamp="timestamp"
    :show-actions="showMenu"
  >
    <div class="_comment-container">
      <m-editor
        ref="editor"
        :key="editorKey"
        v-model:value="message"
        :class="['_editor', $store.state.breakpoint.smAndDown ? '-mobile' : '']"
        :allowed-content="allowedContent"
        :disabled="disabled"
        :ctrl-enter-handlers="[onCmdOrModEnter]"
        :mod-enter-handlers="[onCmdOrModEnter]"
        :initial-value="comment.message"
        @focus="focused = true"
        @blur="blur"
      />
      <div
        v-if="!disabled && (focused || messageDirty)"
        class="_submit"
      >
        <m-btn
          color="primary"
          icon="send"
          round
          xs
          fab
          :disabled="!messageDirty"
          :loading="updateCommentLoading"
          @click="submit"
        />
      </div>
    </div>
    <template
      v-if="disabled && canEdit"
      #actions
    >
      <m-dropdown
        v-model:value="showMenu"
        :title="$t('general.actions')"
        close-on-click
      >
        <m-tooltip>
          <m-btn
            :hide-border="!$store.state.breakpoint.smAndDown"
            icon="ellipsis"
            fab
            light
            small
            @click="showMenu = true"
          />
          <template #title>
            {{ $t('general.moreActions') }}
          </template>
        </m-tooltip>
        <template #overlay>
          <m-card
            list
            no-padding
          >
            <m-card-item
              icon="edit"
              @click="edit"
            >
              {{ $t('general.edit') }}
            </m-card-item>
            <m-card-item
              icon="delete"
              @click="confirmDelete"
            >
              {{ $t('general.delete') }}
            </m-card-item>
          </m-card>
        </template>
      </m-dropdown>
    </template>
  </comment-layout>
</template>

<script>
import CommentLayout from '@/components/comment/CommentLayout.vue';
import MEditor from '@/components/editor/MEditor.vue';
import isEqual from 'lodash-es/isEqual';
import useAccess from '@/composables/access/access';
import useComments from '@/composables/comments/comments';
import useLoggedInUser from '@/composables/logged-in-user/logged-in-user';
import { DateTime } from 'luxon';
import { editorNodeType, featureFlag } from 'shared/constants.json';
import { trimEmptyLines } from '@/lib/editor/editor';

export default {
  name: 'CommentItem',
  props: {
    comment: {
      type: Object,
      required: true,
    },
  },
  components: {
    CommentLayout,
    MEditor,
  },
  setup() {
    const allowedContent = [
      editorNodeType.codeBlock,
      editorNodeType.blockquote,
      editorNodeType.bulletList,
      editorNodeType.orderedList,
      editorNodeType.todoList,
      editorNodeType.mention,
      editorNodeType.goalMention,
    ];

    const { accountHasFeature } = useAccess();
    if (accountHasFeature([featureFlag.fileUpload])) {
      allowedContent.push(editorNodeType.image, editorNodeType.file);
    }

    const { updateComment, updateCommentLoading, deleteComment } = useComments();
    const { loggedInUser } = useLoggedInUser();
    return {
      updateComment,
      updateCommentLoading,
      deleteComment,
      loggedInUser,
      allowedContent,
    };
  },
  data() {
    return {
      disabled: true,
      focused: false,
      message: null,
      editorKey: 0,
      showMenu: false,
    };
  },
  computed: {
    canEdit() {
      if (this.comment.creator === null) {
        return false;
      }

      return this.loggedInUser.uid === this.comment.creator.uid;
    },
    timestamp() {
      return DateTime.fromISO(this.comment.createdAt).toLocaleString(DateTime.DATETIME_MED);
    },
    messageDirty() {
      return !isEqual(this.message, this.comment.message);
    },
  },
  methods: {
    onCmdOrModEnter() {
      if (this.messageDirty) {
        this.submit();
      }
      return true;
    },
    edit() {
      this.showMenu = false;
      this.disabled = false;
      this.$nextTick(() => {
        this.$refs.editor.focus();
      });
    },
    blur() {
      this.message = trimEmptyLines(this.message);
      if (this.$refs.editor !== undefined) {
        this.$refs.editor.setContent(this.message);
      }
      this.focused = false;
      if (!this.messageDirty) {
        this.disabled = true;
      }
    },
    submit() {
      this.updateComment({
        ...this.comment,
        message: this.message,
      }).then(() => {
        this.editorKey += 1;
        this.disabled = true;
      });
    },
    confirmDelete() {
      this.showMenu = false;
      const deleteMethod = () => {
        this.deleteComment(this.comment.uid).catch(() => {
          this.$showSnackbar({ color: 'error', message: this.$t('error.default') });
        });
      };

      this.$confirm({
        title: this.$t('commentItem.deletePrompt'),
        okText: this.$t('general.yesDelete'),
        okType: 'danger',
        maskClosable: true,
        cancelText: this.$t('general.cancel'),
        onOk() {
          deleteMethod();
        },
      });
    },
  },
  watch: {
    comment: {
      handler(val) {
        this.message = val.message;
      },
      deep: true,
    },
  },
  created() {
    this.message = this.comment.message;
  },
};
</script>

<style scoped lang="scss" type="text/scss">
  .comment-item {
    ._comment-container {
      display: flex;
      flex-wrap: wrap;
      gap: .4rem .6rem;
      justify-content: flex-end;
      align-items: flex-end;

      ._editor {
        flex-grow: 1;
        min-width: 0;

        &.-mobile {
          margin-right: 3rem;
        }
      }

      ._submit {
        padding-bottom: .4rem;
      }
    }
  }
</style>
